import React, { useEffect, useState } from 'react';
import cn from 'classnames';
import styles from './Auth.module.sass';
import AuthForm, {
  AuthFormState,
} from 'components/views/Auth/AuthForm/AuthForm';
import { useRouter } from 'next/router';
import { useAuth } from 'hooks/useAuth';
import ROUTES from 'config/routes';
import { APPLICATION_STATUSES, isCreator } from 'types/creator';
import { getAuthRedirect } from 'components/containers/AuthProtection';
import Button from 'components/common/Button';
import Icon from 'components/common/Icon';
import {
  alternateSignUpButtonLabel,
  alternateSignUpUrl,
  isAlternateSignUpEnabled,
} from 'constants/alternateSignUp';

const AuthView: React.FC = () => {
  const router = useRouter();
  const { isLoading, authProfile } = useAuth();
  const [authFormState, setAuthFormState] = useState<AuthFormState>('sign-up');

  const toggleAuthFormState = () => {
    setAuthFormState((prev) => (prev === 'sign-up' ? 'login' : 'sign-up'));
  };

  useEffect(() => {
    if (!isLoading && typeof authProfile !== 'undefined') {
      const sessionRedirect = getAuthRedirect();
      if (typeof sessionRedirect === 'string') {
        router.push(sessionRedirect);
        return;
      }
      const { creator } = authProfile;
      if (
        isCreator(creator) &&
        creator.applicationStatus !== APPLICATION_STATUSES.NEW
      ) {
        router.push(ROUTES.ApplicationStatus.getPath());
      } else {
        router.push(ROUTES.Registration.getPath());
      }
    }
  }, [isLoading, router, authProfile]);

  const showAlternateSignUp =
    authFormState === 'sign-up' &&
    isAlternateSignUpEnabled &&
    !!alternateSignUpUrl;

  return (
    <div
      className={cn(styles.content, {
        [styles.contentAlternate]: showAlternateSignUp,
      })}
    >
      {authFormState === 'sign-up' ? (
        showAlternateSignUp ? (
          <h1 className={styles.title}>
            Shoot UGC content,
            <br />
            make cash
          </h1>
        ) : (
          <>
            <h1 className={styles.title}>
              Shoot content,
              <br />
              make cash
            </h1>
            <p className={styles.subtitle}>
              We add photo, video, and social media brand partnerships daily.
            </p>
          </>
        )
      ) : (
        <h1 className={styles.title}>Welcome back!</h1>
      )}
      {showAlternateSignUp ? (
        <>
          <div className={styles.alternateCard}>
            <div className={styles.alternateBadge}>
              <span className={styles.alternateBadgeDot} aria-hidden />
              Now Accepting Applications
            </div>
            <h2 className={styles.alternateCardTitle}>
              Our creator applications have moved to soona
            </h2>
            <p className={styles.alternateCardBody}>
              Trend&apos;s creator network is now part of soona. We brought the
              community, the way campaigns run, and everything that made it
              work, then evolved it with a better creator experience.
            </p>
            <Button
              className={cn(styles.authContainer, styles.alternatePrimaryCta)}
              type="primary"
              externalLink
              href={alternateSignUpUrl}
            >
              <span className={styles.alternateCtaInner}>
                {alternateSignUpButtonLabel}
                <Icon
                  name="arrow-forward"
                  size={20}
                  color="neutral08"
                  className={styles.alternateCtaArrow}
                />
              </span>
            </Button>
          </div>
          <Button
            type="outline"
            text="Already Have An Account? Sign In"
            className={cn(styles.authContainer, styles.alternateSignIn)}
            onClick={toggleAuthFormState}
          />
        </>
      ) : (
        <AuthForm
          className={styles.authContainer}
          authFormState={authFormState}
          toggleFormState={toggleAuthFormState}
        />
      )}
    </div>
  );
};

export default AuthView;
